/**
 * GroupByPills — Sprint 2 (FE-9.4).
 *
 * Pill row pinned at the top of Browse (above the search bar).
 * Five options:
 *   [Recent] [Scenes] [Location] [Crew] [Tag]
 *
 * Selection persists per **session** (`sessionStorage`), not across
 * sessions. Rationale from the issue: surprising users who close +
 * reopen the app and find they're "stuck" in Scenes mode is worse
 * than asking them to re-pick. Fresh tab = Recent default.
 */

import React, { useCallback } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { fpColors } from '../../../styles/theme';
import type { BrowseLayout } from '../groupItemsBy';

const PILLS: Array<{ id: BrowseLayout; label: string }> = [
  { id: 'recent', label: 'Recent' },
  { id: 'scenes', label: 'Snapshots' },
  { id: 'location', label: 'Location' },
  // FE-201 — pill label renamed; internal id 'crew' kept to avoid
  // breaking the BrowseLayout union + persisted sessionStorage state.
  { id: 'crew', label: 'Circle' },
  { id: 'tag', label: 'Tag' },
];

const SESSION_KEY = '@founsi/browse_layout';

export interface GroupByPillsProps {
  value: BrowseLayout;
  onChange: (next: BrowseLayout) => void;
}

export const GroupByPills: React.FC<GroupByPillsProps> = ({
  value,
  onChange,
}) => {
  const handlePress = useCallback(
    (next: BrowseLayout) => {
      if (next !== value) {
        onChange(next);
        try {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(SESSION_KEY, next);
          }
        } catch {
          /* SSR / private mode — fall through silently */
        }
      }
    },
    [value, onChange],
  );

  return (
    <View
      accessibilityRole="tablist"
      accessibilityLabel="Browse layout"
      style={styles.wrap}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scroll}
      >
        {PILLS.map((p) => {
          const selected = p.id === value;
          return (
            <Pressable
              key={p.id}
              onPress={() => handlePress(p.id)}
              accessibilityRole="tab"
              accessibilityState={{ selected }}
              accessibilityLabel={`${p.label} layout`}
              style={({ pressed }) => [
                styles.pill,
                selected && styles.pillSelected,
                pressed && !selected && styles.pillPressed,
              ]}
            >
              <Text
                style={[
                  styles.pillText,
                  selected && styles.pillTextSelected,
                ]}
              >
                {p.label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
};

/** Read the persisted layout. Returns 'recent' if nothing valid is
 *  stored. Safe to call from a useState initializer. */
export function readPersistedLayout(): BrowseLayout {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const v = window.sessionStorage.getItem(SESSION_KEY);
      if (
        v === 'recent' ||
        v === 'scenes' ||
        v === 'location' ||
        v === 'crew' ||
        v === 'tag'
      ) {
        return v;
      }
    }
  } catch {
    /* fall through to default */
  }
  return 'recent';
}

/** Test-only escape hatch — clear the persisted decision. */
export function __clearPersistedLayoutForTests(): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    /* noop */
  }
}

const styles = StyleSheet.create({
  wrap: {
    backgroundColor: fpColors.background,
  },
  scroll: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8,
  },
  pill: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  pillSelected: {
    backgroundColor: fpColors.primary,
    borderColor: fpColors.primary,
  },
  pillPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  pillText: {
    fontSize: 13,
    fontWeight: '600',
    color: fpColors.onSurface,
  },
  pillTextSelected: {
    color: fpColors.onPrimary,
  },
});

export default GroupByPills;
